import { type FieldValues, type UseFormReturn } from 'react-hook-form';
import {
  BorrowerInputVariables,
  InputLabel,
  PortalType,
  STATES,
} from '@sparrow-card/sparrow-card-constants';
import { MIN_ZIP_LENGTH, PHONE_FORMAT, SSN_FORMAT } from 'components/Input/templates';
import { StaticErrorMessage } from 'enums/Messages';
import { getIsRequiredMessage, isMinLength } from 'utils/error';
import { customEmailValidation, formValidation, RegExpCollection } from 'utils/validation';

const MIN_TEXT_LENGTH: number = 2;

export function registerFields(
  methods: UseFormReturn<FieldValues>,
  serverErrors: Partial<Record<BorrowerInputVariables, string>>,
  setServerErrors: React.Dispatch<
    React.SetStateAction<Partial<Record<BorrowerInputVariables, string>>>
  >,
  formRequiredFields: Partial<Record<BorrowerInputVariables, boolean>>,
  flow: PortalType,
) {
  const { register, setValue, clearErrors } = methods;

  register(BorrowerInputVariables.FirstName, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.FirstName] ?? false,
      message: getIsRequiredMessage(InputLabel.FirstName),
    },
    pattern: { value: RegExpCollection.firstLastName, message: StaticErrorMessage.FirstLastName },
    minLength: { message: isMinLength(MIN_TEXT_LENGTH), value: MIN_TEXT_LENGTH },
  });

  register(BorrowerInputVariables.LastName, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.LastName] ?? false,
      message: getIsRequiredMessage(InputLabel.LastName),
    },
    pattern: { value: RegExpCollection.firstLastName, message: StaticErrorMessage.FirstLastName },
    minLength: { message: isMinLength(MIN_TEXT_LENGTH), value: MIN_TEXT_LENGTH },
  });

  register(BorrowerInputVariables.MiddleInitial, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.MiddleInitial] ?? false,
      message: getIsRequiredMessage(InputLabel.MiddleInitial),
    },
    pattern: { value: RegExpCollection.onlyLatin, message: StaticErrorMessage.OnlyLatinLetters },
  });

  register(BorrowerInputVariables.Phone, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.Phone] ?? false,
      message: getIsRequiredMessage(InputLabel.Phone),
    },
    onChange: () => {
      setServerErrors((prev) => ({ ...prev, [BorrowerInputVariables.Phone]: undefined }));
    },
    validate: (phoneValue: string) => {
      const serverPhoneError = serverErrors[BorrowerInputVariables.Phone];

      if (phoneValue.length === PHONE_FORMAT.length && serverPhoneError) {
        return serverPhoneError;
      }

      return formValidation.phone(phoneValue, InputLabel.Phone);
    },
  });

  register(BorrowerInputVariables.DateOfBirth, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.DateOfBirth] ?? false,
      message: getIsRequiredMessage(InputLabel.DateOfBirth),
    },
    validate: (value) => formValidation.datePicker(value, InputLabel.Email),
  });

  register(BorrowerInputVariables.SSN, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.SSN] ?? false,
      message: getIsRequiredMessage(InputLabel.SSN),
    },
    onChange: () => {
      setServerErrors((prev) => {
        const next = { ...prev };

        delete next[BorrowerInputVariables.SSN];
        delete next[BorrowerInputVariables.Phone];

        return next;
      });
      clearErrors(BorrowerInputVariables.Phone);
    },
    validate: (ssnValue: string) => {
      if (ssnValue.length === SSN_FORMAT.length && serverErrors[BorrowerInputVariables.SSN]) {
        return StaticErrorMessage.SSNNotMatchOfferData;
      }

      return formValidation.ssn(ssnValue, InputLabel.SSN);
    },
  });

  register(BorrowerInputVariables.Street, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.Street] ?? false,
      message: getIsRequiredMessage(InputLabel.Street),
    },
    pattern: {
      value: RegExpCollection.streetAddressAndUnit,
      message: StaticErrorMessage.StreetAddressAndUnit,
    },
    validate: (value) => formValidation.streetAddress(value, flow),
  });

  register(BorrowerInputVariables.Unit, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.Unit] ?? false,
      message: getIsRequiredMessage(InputLabel.Unit),
    },
    pattern: {
      value: RegExpCollection.streetAddressAndUnit,
      message: StaticErrorMessage.StreetAddressAndUnit,
    },
    validate: (value) => formValidation.streetAddress(value, flow),
  });

  register(BorrowerInputVariables.City, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.City] ?? false,
      message: getIsRequiredMessage(InputLabel.City),
    },
    pattern: { value: RegExpCollection.city, message: StaticErrorMessage.City },
    minLength: { message: isMinLength(MIN_TEXT_LENGTH), value: MIN_TEXT_LENGTH },
  });

  register(BorrowerInputVariables.State, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.State] ?? false,
      message: getIsRequiredMessage(InputLabel.State),
    },
    validate: (value) => {
      const stateValues = STATES.map((state) => state.value);

      return stateValues.includes(value) || 'Invalid state selected';
    },
    onChange(e) {
      setValue(BorrowerInputVariables.State, e.target.value.toUpperCase());
    },
  });

  register(BorrowerInputVariables.Zip, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.Zip] ?? false,
      message: getIsRequiredMessage(InputLabel.Zip),
    },
    validate: (value) => formValidation.zip(value, MIN_ZIP_LENGTH),
  });

  register(BorrowerInputVariables.TotalMonthlyIncome, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.TotalMonthlyIncome] ?? false,
      message: getIsRequiredMessage(InputLabel.TotalMonthlyIncome),
    },
  });

  register(BorrowerInputVariables.CheckBoxTerms, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.CheckBoxTerms] ?? false,
      message: StaticErrorMessage.UnCheckedCheckBox,
    },
  });

  register(BorrowerInputVariables.CheckBoxElectronicCommunications, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.CheckBoxElectronicCommunications] ?? false,
      message: StaticErrorMessage.UnCheckedCheckBox,
    },
  });

  register(BorrowerInputVariables.CheckBoxContracted, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.CheckBoxContracted] ?? false,
      message: StaticErrorMessage.UnCheckedCheckBox,
    },
  });

  register(BorrowerInputVariables.CheckBoxCreditReport, {
    required: {
      value: formRequiredFields[BorrowerInputVariables.CheckBoxCreditReport] ?? false,
      message: StaticErrorMessage.UnCheckedCheckBox,
    },
  });

  if (flow === PortalType.Standard || flow === PortalType.DirectMail) {
    register(BorrowerInputVariables.Email, {
      required: {
        value: formRequiredFields[BorrowerInputVariables.Email] ?? false,
        message: getIsRequiredMessage(InputLabel.Email),
      },
      pattern: {
        value: RegExpCollection.email,
        message: StaticErrorMessage.InvalidEmail,
      },
      validate: (email) => customEmailValidation(email) || StaticErrorMessage.InvalidEmail,
    });
  }

  if (flow === PortalType.Reinstatement) {
    register(BorrowerInputVariables.IsNewCardRequired, {
      required: formRequiredFields[BorrowerInputVariables.IsNewCardRequired] ?? false,
    });
  }
}
